"use client";
import React, { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';

interface FAQSectionProps {
  isVisible: boolean;
  onVisibilityChange: (visible: boolean) => void;
}

export default function FAQSection({ isVisible, onVisibilityChange }: FAQSectionProps) {
  const sectionRef = useRef(null);
  const t = useTranslations();
  const [activeFAQ, setActiveFAQ] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onVisibilityChange(true);
        }
      },
      {
        threshold: 0.02,
        rootMargin: '0px 0px 50px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [onVisibilityChange]);

  return (
    <>
      {/* FAQ Section Styles */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 768px) {
          .section-padding {
            padding: 60px 16px !important;
          }
          
          .section-title {
            font-size: 28px !important;
          }
          
          .faq-button {
            flex-direction: column !important;
            text-align: center !important;
            gap: 16px !important;
          }
          
          .faq-button span {
            margin-right: 0 !important;
          }
          
          .faq-answer {
            padding-right: 0 !important;
          }
        }

        @media (max-width: 480px) {
          .section-title {
            font-size: 24px !important;
          }
          
          .section-padding {
            padding: 50px 12px !important;
          }
          
          .faq-button {
            font-size: 16px !important;
          }
        }
      `}} />

      <section 
        ref={sectionRef} 
        style={{
          background: "#000",
          padding: "80px 24px",
          position: "relative",
        }}
        className="section-padding"
      >
        <div style={{
          maxWidth: "800px",
          margin: "0 auto",
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
          transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      transitionDelay: "0.1s",
        }}>
          {/* Header */}
          <div style={{
            textAlign: "center",
            marginBottom: "60px",
          }}>
            <h2 style={{
              fontSize: "36px",
              fontWeight: 600,
              color: "#fff",
              lineHeight: 1.2,
              marginBottom: "24px",
            }}
            className="section-title">
              {t('oldFurnitureRemoval.faq.title')}
            </h2>

            <p style={{
              fontSize: "18px",
              color: "rgba(255,255,255,0.8)",
              lineHeight: 1.6,
              maxWidth: "600px",
              margin: "0 auto",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.2s",
            }}>
              {t('oldFurnitureRemoval.faq.description')}
            </p>
          </div>

          {/* FAQ Items */}
          <div style={{
            display: "flex",
            flexDirection: "column",
          }}>
            {/* All 5 FAQ Items */}
            {[
              { key: '0', index: 0 },
              { key: '1', index: 1 },
              { key: '2', index: 2 },
              { key: '3', index: 3 },
              { key: '4', index: 4 }
            ].map(({ key, index }) => (
              <div key={index}>
                <div style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
                  transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  transitionDelay: `${0.7 + (index * 0.1)}s`,
                }}>
                  <div style={{
                    padding: "24px 0",
                  }}>
                    <button
                      onClick={() => setActiveFAQ(activeFAQ === index ? null : index)}
                      className="faq-button"
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        background: "none",
                        border: "none",
                        color: "#fff",
                        fontSize: "18px",
                        fontWeight: 500,
                        textAlign: "left",
                        cursor: "pointer",
                        padding: "0",
                        lineHeight: 1.4,
                      }}
                    >
                      <span style={{ marginRight: "20px" }}>
                        {t(`oldFurnitureRemoval.faq.items.${key}.question`)}
                      </span>
                      
                      <div style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        border: "2px solid #262626",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        transition: "all 0.3s ease",
                        backgroundColor: activeFAQ === index ? "#D90D34" : "transparent",
                        borderColor: activeFAQ === index ? "#D90D34" : "#262626",
                      }}>
                        <span style={{
                          fontSize: "20px",
                          fontWeight: 300,
                          color: "#fff",
                          lineHeight: 1,
                          transition: "transform 0.3s ease",
                          transform: activeFAQ === index ? "rotate(45deg)" : "rotate(0deg)",
                        }}>
                          +
                        </span>
                      </div>
                    </button>

                    {activeFAQ === index && (
                      <div 
                        className="faq-answer"
                        style={{
                          marginTop: "20px",
                          paddingRight: "60px",
                          animation: "fadeIn 0.3s ease",
                        }}
                      >
                        <p style={{
                          fontSize: "16px",
                          color: "rgba(255,255,255,0.8)",
                          lineHeight: 1.6,
                          margin: 0,
                        }}>
                          {t(`oldFurnitureRemoval.faq.items.${key}.answer`)}
                        </p>
                      </div>
                    )}
                  </div>

                  {index < 4 && (
                    <div style={{
                      height: "1px",
                      background: "#262626",
                      width: "100%",
                    }} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
} 